/**
 * MSAL.js configuración para login OIDC contra Microsoft Entra ID.
 *
 * Los valores se inyectan en build time via Vite env vars:
 *   VITE_ENTRA_TENANT_ID  — tenant ID del directorio Entra
 *   VITE_ENTRA_CLIENT_ID  — application ID de la app OIDC registrada
 *
 * Si no están configurados en build, MSAL devuelve un error al hacer login.
 */
import {
  type Configuration,
  EventType,
  InteractionRequiredAuthError,
  PublicClientApplication,
} from "@azure/msal-browser";

const TENANT_ID = import.meta.env.VITE_ENTRA_TENANT_ID || "";
const CLIENT_ID = import.meta.env.VITE_ENTRA_CLIENT_ID || "";

/**
 * Si `VITE_DEV_BYPASS_AUTH=true`, MSAL no se inicializa y la app asume
 * un usuario fake (`VITE_DEV_USER_EMAIL`). El backend debe correr con
 * `ENV=dev` y `DEV_USER_EMAIL=...` para aceptar requests sin Bearer token.
 */
export const DEV_BYPASS_AUTH =
  import.meta.env.VITE_DEV_BYPASS_AUTH === "true";

export const DEV_USER_EMAIL =
  import.meta.env.VITE_DEV_USER_EMAIL || "dev@local";

export const msalConfig: Configuration = {
  auth: {
    clientId: CLIENT_ID,
    authority: TENANT_ID
      ? `https://login.microsoftonline.com/${TENANT_ID}`
      : "https://login.microsoftonline.com/common",
    // Redirect en la misma URL — MSAL maneja el handshake con popup.
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
  },
  cache: {
    // localStorage: la sesión sobrevive pestañas nuevas y reinicios del
    // navegador (sessionStorage moría con cada pestaña).
    cacheLocation: "localStorage",
    storeAuthStateInCookie: false,
  },
};

export const loginRequest = {
  scopes: ["openid", "profile", "email", "User.Read"],
};

export const msalInstance = new PublicClientApplication(msalConfig);

/**
 * SSO entre los sitios de MPP (home, etrm, portal-edocta, organizacion,
 * escribano). Cada sitio es otro origen y no ve la caché de MSAL de los demás,
 * pero todos usan la misma app de Entra: si el navegador ya tiene sesión de
 * Microsoft, `ssoSilent` la reutiliza sin mostrar nada.
 * Si el navegador bloquea ese iframe (cookies de terceros) y se llegó desde una
 * liga del home (`?sso=1`), se hace un redirect completo: con la sesión de
 * Microsoft viva, Entra regresa solo, sin pedir credenciales. El home manda
 * además `login_hint` para que Entra no pregunte qué cuenta usar.
 */
async function intentarSso(): Promise<void> {
  if (window.self !== window.top) return; // somos el iframe del propio ssoSilent
  const url = new URL(window.location.href);
  const desdeHome = url.searchParams.get("sso") === "1";
  const loginHint = url.searchParams.get("login_hint") || undefined;
  if (url.searchParams.has("sso") || url.searchParams.has("login_hint")) {
    url.searchParams.delete("sso");
    url.searchParams.delete("login_hint");
    window.history.replaceState(window.history.state, "", url.toString());
  }
  if (msalInstance.getAllAccounts().length) return;
  try {
    const r = await msalInstance.ssoSilent({ ...loginRequest, loginHint });
    msalInstance.setActiveAccount(r.account);
  } catch {
    if (desdeHome) {
      // Regresa a la misma página (ya sin ?sso, así que no hay ciclo).
      await msalInstance.loginRedirect({ ...loginRequest, loginHint, redirectStartPage: url.toString() });
    }
  }
}

// Inicializar la instancia (MSAL v3+ requiere initialize() antes de usar).
export async function initMsal(): Promise<void> {
  await msalInstance.initialize();

  // Si se recargó la página con una respuesta OIDC, procesarla.
  await msalInstance.handleRedirectPromise();

  // Asegurar que haya una cuenta activa para los hooks/helpers.
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length && !msalInstance.getActiveAccount()) {
    msalInstance.setActiveAccount(accounts[0]);
  }

  await intentarSso();

  msalInstance.addEventCallback((event) => {
    if (
      event.eventType === EventType.LOGIN_SUCCESS &&
      event.payload &&
      "account" in event.payload &&
      event.payload.account
    ) {
      msalInstance.setActiveAccount(event.payload.account);
    }
  });
}

/**
 * Manda al usuario a Entra a renovar la sesión (redirect completo; si su
 * sesión de Microsoft sigue viva regresa solo, si no ve la pantalla de login).
 * Guarda anti-loop: si ya redirigimos hace <1 min no reintenta, para que un
 * 401 persistente (p.ej. backend mal configurado) no recargue en círculo.
 */
const RELOGIN_TS_KEY = "portal.relogin_ts";

export async function renovarSesion(): Promise<void> {
  if (DEV_BYPASS_AUTH) return;
  try {
    const previo = Number(sessionStorage.getItem(RELOGIN_TS_KEY) ?? 0);
    if (Date.now() - previo < 60_000) return;
    sessionStorage.setItem(RELOGIN_TS_KEY, String(Date.now()));
  } catch {
    // sessionStorage bloqueado: redirigimos de todos modos
  }
  const account = msalInstance.getActiveAccount() ?? undefined;
  await msalInstance.acquireTokenRedirect({ ...loginRequest, account });
}

/**
 * Obtiene un id_token fresco (refresca silenciosamente si expiró).
 * Si la sesión murió (refresh token vencido/revocado), redirige al login.
 */
export async function acquireIdToken(): Promise<string> {
  const account = msalInstance.getActiveAccount();
  if (!account) {
    await renovarSesion();
    throw new Error("Sin sesión activa. El usuario debe loguearse.");
  }
  try {
    const result = await msalInstance.acquireTokenSilent({
      ...loginRequest,
      account,
    });
    return result.idToken;
  } catch (e) {
    if (e instanceof InteractionRequiredAuthError) {
      await renovarSesion();
    }
    throw e;
  }
}

export function currentEmail(): string | null {
  if (DEV_BYPASS_AUTH) return DEV_USER_EMAIL;
  return msalInstance.getActiveAccount()?.username ?? null;
}
